/* Mirrors current purchase eligibility and existing product notices; never supplies an ETA. */
if (!customElements.get('mms-shipping-assurance')) {
  customElements.define('mms-shipping-assurance', class extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('product-info, #bundle-offer');
      if (!this.root) return;
      this.label = this.querySelector('strong');
      this.detail = this.querySelector('small');
      this.defaults = this.defaults || {label: this.label.textContent, detail: this.detail.textContent};
      this.sourceTrusted = true;
      this.queue = () => { if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.sync(); }); };
      this.onChange = event => {
        if (event.target.closest('variant-selects')) {
          this.pendingVariant = this.root.querySelector('mms-preorder-state')?.dataset.variantId;
          this.sourceTrusted = false;
        }
        this.queue();
      };
      this.root.addEventListener('change', this.onChange);
      this.root.addEventListener('input', this.queue);
      this.observer = new MutationObserver(records => {
        if (records.every(record => this.contains(record.target))) return;
        const state = this.root.querySelector('mms-preorder-state');
        const currentId = this.currentIdentity(state);
        const current = state && String(currentId) === state.dataset.variantId && (!this.pendingVariant || state.dataset.variantId !== this.pendingVariant);
        if (current && records.some(record => {
          const target = record.target.nodeType === 1 ? record.target : record.target.parentElement;
          return target?.closest('.restock-rocket-preorder-description') || [...record.addedNodes].some(node => node.nodeType === 1 && (node.matches('.restock-rocket-preorder-description') || node.querySelector('.restock-rocket-preorder-description')));
        })) this.sourceTrusted = true;
        this.queue();
      });
      this.observer.observe(this.root, {subtree:true,childList:true,characterData:true,attributes:true,attributeFilter:['data-variant-id','data-oos','data-allowed','data-mms-reserve','disabled','aria-disabled','hidden','class','style']});
      this.sync();
    }
    currentIdentity(state) {
      const input = state?.closest('form')?.querySelector('[name="id"]');
      // Completed fixed bundles commit a mapped parent ID and its inventory state together.
      // Prefer that confirmed current identity over the pre-selection hardware fallback.
      if (input && !input.disabled && input.value && String(input.value) === state?.dataset.variantId) return input.value;
      const bundle = this.root.querySelector('mila-device-bundle[data-fixed-three="true"]');
      if (bundle) {
        // Fixed bundles leave the native parent ID empty until oils are selected.
        // Match the same server-rendered hardware source used by the diffuser controller.
        const source = bundle.dataset.sourceVariant;
        try {
          const variants = JSON.parse(this.root.querySelector('mms-diffuser-atc script[type="application/json"]')?.textContent || '[]');
          if (variants.some(variant => String(variant.id) === String(source))) return source;
        } catch (_) { return null; }
        return null;
      }
      return state?.closest('form')?.querySelector('[name="id"]')?.value;
    }
    visible(node) {
      return Boolean(node && !node.hidden && node.getClientRects().length && getComputedStyle(node).display !== 'none' && getComputedStyle(node).visibility !== 'hidden');
    }
    timing(nodes) {
      // Copy actual messaging only; no generic device/oil wait-time fallback.
      const text = nodes.map(node => (node.textContent || '').trim().replace(/\s+/g, ' ')).find(value => /\d/.test(value) && /days?|weeks?|months?|ship|deliver/i.test(value));
      return text ? text.replace(/^(?:reserve\s+today\s*[•·:—-]\s*|preorder\s*:\s*)/i, '') : 'See preorder details';
    }
    state() {
      if (this.root.matches('#bundle-offer')) {
        const tier = this.root.querySelector('#empCompactTier')?.value || '1';
        const button = this.root.querySelector('#tier' + tier + 'Btn');
        if (!button || button.disabled || /unavailable|sold\s*out/i.test(button.textContent)) return {mode:'unavailable'};
        if (button.hasAttribute('data-mms-reserve')) {
          const notice = button.nextElementSibling;
          return {mode:'preorder', timing:this.timing(notice?.matches('[data-emp-preorder-eta]:not([hidden])') ? [notice] : [])};
        }
        return {mode:'available'};
      }
      const state = this.root.querySelector('mms-preorder-state');
      const form = state?.closest('form');
      const button = form?.querySelector('.product-form__submit');
      const id = this.currentIdentity(state);
      const fixedBundle = this.root.querySelector('mila-device-bundle[data-fixed-three="true"]');
      const nativeInput = form?.querySelector('[name="id"]');
      // An incomplete native bundle is not the legacy preset whose stock may differ.
      if (fixedBundle && (!nativeInput?.value || nativeInput.disabled) && state?.dataset.oos !== 'true') return {mode:'pending'};
      if (!state || !button || String(id) !== state.dataset.variantId || (this.pendingVariant && state.dataset.variantId === this.pendingVariant)) return {mode:'pending'};
      this.pendingVariant = null;
      if (/unavailable|sold\s*out/i.test(button.textContent) || (state.dataset.oos === 'true' && state.dataset.allowed !== 'true')) return {mode:'unavailable'};
      const reserve = button.hasAttribute('data-mms-reserve') || (state.dataset.oos === 'true' && state.dataset.allowed === 'true');
      if (reserve) {
        const app = [...this.root.querySelectorAll('.restock-rocket-preorder-description')].filter(node => this.visible(node));
        const native = [...form.querySelectorAll('[data-mms-preorder-eta]:not([hidden])')].filter(node => this.visible(node));
        return {mode:'preorder', timing:this.timing(app.length ? (this.sourceTrusted ? app : []) : native)};
      }
      if (button.disabled || button.getAttribute('aria-disabled') === 'true') return {mode:'pending'};
      return {mode:'available'};
    }
    sync() {
      const state = this.state();
      const content = state.mode === 'available' ? this.defaults : state.mode === 'preorder' ? {label:'Preorder', detail:state.timing} : state.mode === 'unavailable' ? {label:'Unavailable',detail:'Choose another option'} : {label:'Shipping',detail:'Select an available option'};
      if (this.label.textContent !== content.label) this.label.textContent = content.label;
      if (this.detail.textContent !== content.detail) this.detail.textContent = content.detail;
      this.detail.hidden = !content.detail;
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.root?.removeEventListener('change', this.onChange);
      this.root?.removeEventListener('input', this.queue);
      if (this.frame) cancelAnimationFrame(this.frame);
    }
  });
}
